import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useApolloClient, gql } from '@apollo/client';
import Container from '../Container';
import Loader from '../Loader';
import UploadIcon from './UploadIcon';

const moderateAssetQuery = gql`
  query moderateAsset($url: String!) {
    moderateAsset(url: $url)
  }
`;

const uploadAssetQuery = gql`
  query uploadAssetUrl($type: String!) {
    uploadAssetUrl(type: $type)
  }
`;

const StyledLabel = styled.label`
  display: block;
  cursor: pointer;
  position: relative;
`;

const StyledInput = styled.input`
  display: none;
`;

const StyledContainer = styled(Container)`
  pointer-events: none;
`;

const Uploader = ({
  onUploadSuccess,
  name,
  accept,
  multiple,
  children,
  disabled,
  maxUploads,
  maxFileSize,
  onError,
  style,
  moderate,
  isLoading,
}) => {
  const [loading, setLoading] = useState(false);
  const [dragOverActive, setDragOverActive] = useState(false);

  const client = useApolloClient();

  const moderateImage = url => {
    const res = client.query({
      query: moderateAssetQuery,
      variables: { url },
      fetchPolicy: 'no-cache',
    });
    return res;
  };

  const checkModeration = async images => {
    if (!moderate) return false;

    const moderateArray = images.map(async ({ src }) => moderateImage(src));
    const resolved = await Promise.all(moderateArray);

    return resolved.some(({ data: { moderateAsset } }) => !moderateAsset);
  };

  const getImageType = fileName => {
    const extension = fileName
      .split('.')
      .pop()
      .toLowerCase();
    const imageTypes = ['jpg', 'jpeg', 'gif', 'webp', 'png'];
    return {
      type: imageTypes.includes(extension) ? 'image' : extension,
      extension,
    };
  };

  const errorCheck = ({ files, queryError, status, moderation }) => {
    const errorObj = {
      maxUploads: files && files.length > maxUploads,
      maxFileSize:
        files && Array.from(files).some(file => file.size > maxFileSize),
      fileType:
        files &&
        Array.from(files).some(file => {
          const { extension } = getImageType(file.name);
          return !accept.includes(extension);
        }),
      empty: files && !files.length,
      upload: !!queryError || (status && ![201, 204].includes(status)),
      disabledAttempt: disabled,
      badModeration: moderation,
    };

    const error = Object.keys(errorObj).find(key => errorObj[key]);

    const allErrors = {
      ...errorObj,
      error,
    };

    onError(allErrors);

    return allErrors;
  };

  const getSignedUrl = async file => {
    const { type } = getImageType(file.name);

    const { data: { uploadAssetUrl } = {}, error } = await client.query({
      query: uploadAssetQuery,
      variables: {
        type,
      },
      fetchPolicy: 'no-cache',
    });

    const { fields, url } = uploadAssetUrl || {};

    errorCheck({ queryError: error });

    return (
      !error && {
        fields: {
          ...fields,
          'Content-Type': file.type,
          'Content-Disposition': `inline; filename=${file.name}`,
          file,
        },
        url,
      }
    );
  };

  const sendToS3 = async file => {
    const signedUrl = await getSignedUrl(file);

    if (!signedUrl) return false;

    const body = Object.keys(signedUrl.fields).reduce((formData, key) => {
      formData.append(key, signedUrl.fields[key]);
      return formData;
    }, new FormData());

    const res = await fetch(signedUrl.url, {
      method: 'POST',
      body,
    });

    errorCheck({ status: res.status });

    return res.status === 204 && `${signedUrl.url}/${signedUrl.fields.key}`;
  };

  const onFileSelect = async files => {
    if (disabled) return;

    setLoading(true);
    isLoading(true);

    const errorObj = errorCheck({ files });

    if (errorObj.error) {
      setLoading(false);
      isLoading(false);
      return;
    }

    const promiseArray = [];

    await Array.from(files).reduce(async (previousPromise, file, i) => {
      const imageUrl = await previousPromise;

      promiseArray.push({
        src: imageUrl,
        name: file.name,
        media: file.type,
      });

      const nextFile = files[i + 1];

      if (nextFile) {
        return sendToS3(nextFile);
      }
      return null;
    }, sendToS3(files[0]));

    const hasError = promiseArray.some(image => !image.src);
    const moderationError = await checkModeration(promiseArray);
    errorCheck({ moderation: moderationError });

    if (!moderationError && !hasError) {
      onUploadSuccess(promiseArray);
    }

    setLoading(false);
    isLoading(false);
  };

  const onDrop = e => {
    if (disabled) return;
    e.preventDefault();
    setDragOverActive(false);
    onFileSelect(e.dataTransfer.files);
  };

  const onChange = async e => {
    e.persist();
    await onFileSelect(e.target.files);
    e.target.value = null;
  };

  const onDragOver = e => {
    e.preventDefault();

    const { dataTransfer = { effectAllowed: '', types: [] } } = e;

    if (dataTransfer.effectAllowed !== 'all' || !dataTransfer.types.length)
      return;

    setDragOverActive(true);
  };

  const onDragLeave = e => {
    e.preventDefault();
    setDragOverActive(false);
  };

  return (
    <StyledLabel
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      dragOverActive={dragOverActive}
      loading={loading ? 1 : 0}
      htmlFor={name}
      style={style}
    >
      {loading && (
        <Container
          position="absolute"
          left="0"
          top="0"
          bottom="0"
          right="0"
          zIndex="5"
          display="flex"
          justifyContent="center"
          alignItems="center"
          backgroundColor="rgba(255,255,255,0.7)"
        >
          <Loader width="160px" />
        </Container>
      )}
      {dragOverActive && (
        <StyledContainer
          position="absolute"
          left="0"
          top="0"
          bottom="0"
          right="0"
          zIndex="5"
          padding="20px"
        >
          <Container
            backgroundColor="rgba(255,255,255,0.7)"
            border="2px dashed"
            borderColor="#97aeff"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
            borderRadius="10px"
          >
            <UploadIcon />
          </Container>
        </StyledContainer>
      )}
      <StyledInput
        id={name}
        onChange={onChange}
        name={name}
        accept={accept}
        multiple={multiple}
        type="file"
        disabled={disabled}
      />
      {children}
    </StyledLabel>
  );
};

Uploader.propTypes = {
  onUploadSuccess: PropTypes.func,
  name: PropTypes.string,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  maxUploads: PropTypes.number,
  maxFileSize: PropTypes.number,
  onError: PropTypes.func,
  isLoading: PropTypes.func,
  style: PropTypes.shape({}),
  moderate: PropTypes.bool,
};

Uploader.defaultProps = {
  onUploadSuccess: () => {},
  name: '',
  accept: '.jpg,.jpeg,.png',
  multiple: false,
  children: null,
  disabled: false,
  maxUploads: 20,
  maxFileSize: 1024 * 1024 * 10,
  onError: () => {},
  isLoading: () => {},
  style: null,
  moderate: false,
};

export default Uploader;
